"use client";

import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { BriefcaseCompleteIllustration } from "@/components/illustrations/briefcase-complete-illustration";
import { Icon, type IconName } from "@/components/ui/icon";
import { useAuth } from "@/features/auth";
import { useStartTalentSearchFromJob } from "@/hooks/use-start-talent-search-from-job";
import { useArbitratorViewStore } from "@/features/arbitrator/store/view-store";
import { useSequenceWizardStore } from "@/features/sequences/store/wizard-store";
import { useJobWizardStore } from "@/features/jobs/store/job-store";

type CardId = "talent-search" | "sequences" | "arbitrator";

const NEXT_STEP_CARDS: {
  id: CardId;
  icon: IconName;
  title: string;
  description: string;
}[] = [
  {
    id: "talent-search",
    icon: "IconSearchIntelligence",
    title: "Start Sourcing with Talent Search",
    description:
      "Search 240M+ profiles using filters, natural language, or AI-assisted queries to find the right candidates.",
  },
  {
    id: "sequences",
    icon: "IconPaperPlane",
    title: "Create or Clone Your First Outreach Sequence",
    description:
      "Build multi-step, multi-channel outreach sequences with AI-personalized messaging to engage candidates.",
  },
  {
    id: "arbitrator",
    icon: "IconSparklesTwo2",
    title: "Setup Your First Arbitrator",
    description:
      "Define screening criteria and let the Arbitrator automatically evaluate candidates against your requirements.",
  },
];

export function StepComplete() {
  const router = useRouter();
  const { form, createdJobId, createdJobProjectId, resetWizard } =
    useJobWizardStore();
  const { selectedProjectId } = useAuth();
  const projectId = createdJobProjectId ?? selectedProjectId ?? null;
  const { start: startTalentSearch, isPending: isTalentSearchPending } =
    useStartTalentSearchFromJob(projectId);
  const [pendingCard, setPendingCard] = useState<CardId | null>(null);
  const createPendingRef = useRef(false);

  // The wizard is non-dismissible at this step, so a card click is the only
  // exit — reset before navigating or /jobs re-opens the finished wizard.
  const goTo = (path: string) => {
    resetWizard();
    router.push(path);
  };

  const handleTalentSearch = async () => {
    if (createPendingRef.current) return;
    createPendingRef.current = true;
    setPendingCard("talent-search");
    const ok = await startTalentSearch({
      title: form.title,
      description: form.description,
    });
    if (ok) {
      resetWizard();
      return;
    }
    createPendingRef.current = false;
    setPendingCard(null);
  };

  const handleCardClick = (id: CardId) => {
    if (pendingCard || isTalentSearchPending) return;
    if (id === "talent-search") {
      void handleTalentSearch();
    } else if (id === "sequences") {
      if (!createdJobId) {
        goTo("/sequences");
        return;
      }
      useSequenceWizardStore.getState().openWizard(createdJobId);
      goTo(`/jobs/${createdJobId}/sequences`);
    } else {
      if (!createdJobId) {
        goTo("/arbitrators");
        return;
      }
      const stageId = form.stages.find((s) => s.name !== "Archived")?.id ?? null;
      useArbitratorViewStore.getState().openSetupForJob(createdJobId, stageId);
      goTo(`/jobs/${createdJobId}/arbitrator`);
    }
  };

  return (
    <div className="flex flex-col gap-4 items-center justify-center size-full py-4">
      <BriefcaseCompleteIllustration draggable={false} />
      <div className="flex flex-col gap-2 items-center w-full">
        <h3 className="text-xl font-medium text-foreground text-center">
          The job has been successfully created!
        </h3>
        <p className="text-sm text-contrast/80 leading-5 text-center w-full">
          Your job is now live. Start sourcing candidates, set up outreach
          sequences, or configure the Arbitrator to screen profiles
          automatically.
        </p>
      </div>

      {NEXT_STEP_CARDS.map((card) => (
        <button
          key={card.id}
          type="button"
          onClick={() => handleCardClick(card.id)}
          disabled={pendingCard !== null}
          aria-busy={pendingCard === card.id}
          className="bg-contrast/3 border border-border rounded-lg p-3 flex items-center justify-between opacity-[0.96] overflow-clip shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] cursor-pointer hover:bg-contrast/6 transition-colors text-left w-full group disabled:cursor-default disabled:hover:bg-contrast/3"
        >
          <div className="flex-1 flex flex-col gap-1.5 min-w-0">
            <div className="flex items-center justify-between w-full">
              <div className="flex gap-1.5 items-center">
                <div>
                  <Icon
                    name={card.icon}
                    size={16}
                    className="text-ai"
                  />
                </div>
                <span className="text-sm font-medium text-ai">
                  {card.title}
                </span>
              </div>
            </div>
            <p className="text-[13px] text-contrast/70 leading-normal">
              {card.description}
            </p>
          </div>
          <div className="opacity-30 group-hover:opacity-50 transition-opacity shrink-0 ml-3">
            <Icon
              name={
                pendingCard === card.id
                  ? "IconLoadingCircle"
                  : "IconSquareArrowTopRight2"
              }
              size={20}
              className={
                pendingCard === card.id
                  ? "text-foreground animate-spin"
                  : "text-foreground"
              }
            />
          </div>
        </button>
      ))}
    </div>
  );
}
